import { createLogger } from '@/lib/logger';

const log = createLogger('storage');

let requested = false;

/**
 * Ask the OS to exempt this origin's storage (IndexedDB queues, photos,
 * signatures) from automatic eviction. Browsers may otherwise clear site
 * data on their own: iOS evicts storage for origins not used in Safari for
 * 7 days (installed home-screen apps are exempt from that rule but not from
 * low-disk cleanup), and Android can evict under storage pressure. There is
 * no user-facing setting for this; navigator.storage.persist() is the only
 * programmatic protection. Best-effort and idempotent; the grant state is
 * reported in the device heartbeat.
 */
export async function requestPersistentStorage(): Promise<void> {
  if (requested) return;
  requested = true;
  try {
    if (!navigator.storage?.persist) return;
    const already = await navigator.storage.persisted();
    if (already) return;
    const granted = await navigator.storage.persist();
    log.info(granted ? 'persistent storage granted' : 'persistent storage not granted');
  } catch (err) {
    log.debug('persist request failed', err);
  }
}

/**
 * Current protection state for the heartbeat: true = persistent, false =
 * best-effort (evictable), null = API unsupported on this browser.
 */
export async function storagePersisted(): Promise<boolean | null> {
  try {
    if (!navigator.storage?.persisted) return null;
    return await navigator.storage.persisted();
  } catch {
    return null;
  }
}
